import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FleetResponse, FleetCourt, DailyReport, getReportToday, regenerateReport } from '../../api/gestor';
import { Icon, useToast, fmtAgo, serviceLabel } from './ui';

function barClass(court: FleetCourt, service: string): string {
  const state = (court.services || {})[service];
  if (state === 'active') return '';
  if (state === 'activating' || state === 'reloading') return 'warn';
  return 'bad';
}

/* ---------- KPIs ---------- */
export function KpiStrip({ fleet, report }: { fleet?: FleetResponse; report?: DailyReport }) {
  const ag = fleet?.aggregates;
  const videosHoje = report
    ? Object.values(report.videos_by_court || {}).reduce((s: number, n) => s + (Number(n) || 0), 0)
    : null;
  const riscoAlto = report ? report.risk.filter((r) => r.level === 'alto').length : null;
  const riscoMedio = report ? report.risk.filter((r) => r.level === 'medio').length : null;
  return (
    <div className="kpis">
      <div className={`kpi ${ag && ag.offline_count > 0 ? 'alert' : 'good'}`}>
        <div className="klabel"><Icon name="power" size={12} /> Quadras desligadas</div>
        <div className="kval">{ag ? ag.offline_count : '—'}</div>
        <div className="kfoot">de <b>{ag ? ag.total : '—'}</b> quadras conectadas</div>
      </div>
      <div className={`kpi ${ag && ag.degraded_count > 0 ? 'alert' : ''}`}>
        <div className="klabel"><Icon name="alert" size={12} /> Com problema</div>
        <div className="kval">{ag ? ag.degraded_count : '—'}</div>
        <div className="kfoot">services fora do ar ou alerta</div>
      </div>
      <div className="kpi">
        <div className="klabel"><Icon name="chip" size={12} /> CPU média</div>
        <div className="kval">{ag ? `${ag.avg_cpu}%` : '—'}</div>
        <div className={`meter ${ag && ag.avg_cpu > 80 ? 'hot' : ''}`}>
          <i style={{ width: `${Math.min(ag?.avg_cpu || 0, 100)}%` }} />
        </div>
      </div>
      <div className="kpi">
        <div className="klabel"><Icon name="film" size={12} /> Vídeos hoje</div>
        <div className="kval">{videosHoje === null ? '—' : videosHoje}</div>
        <div className="kfoot">temperatura média <b>{ag ? `${ag.avg_temp}°C` : '—'}</b></div>
      </div>
      <div className={`kpi ${riscoAlto ? 'alert' : ''}`}>
        <div className="klabel"><Icon name="search" size={12} /> Previsão de erros</div>
        <div className="kval">{riscoAlto === null ? '—' : riscoAlto + (riscoMedio || 0)}</div>
        <div className="kfoot"><b>{riscoAlto ?? '—'}</b> risco alto · <b>{riscoMedio ?? '—'}</b> médio</div>
      </div>
    </div>
  );
}

/* ---------- saúde das arenas (fica ACIMA do relatório) ---------- */
export function HealthGrid({
  fleet, onOpenCourt,
}: { fleet?: FleetResponse; onOpenCourt: (slug: string) => void }) {
  if (!fleet) return <div className="loading-page"><span className="spinner" /></div>;
  const courts = [...fleet.courts].sort((a, b) => {
    const rank = (c: FleetCourt) => (c.status === 'offline' ? 0 : c.status === 'degraded' ? 1 : 2);
    return rank(a) - rank(b);
  });
  return (
    <div className="health-grid">
      {courts.map((c) => {
        const services = Object.keys(c.services || {});
        const dot = c.status === 'online' ? 'ok' : c.status === 'degraded' ? 'warn' : 'bad';
        return (
          <div key={c.slug} className="hcell" onClick={() => onOpenCourt(c.slug)}>
            <div className="hname">
              <span className={`dot ${dot}`} /> {c.identity?.court || c.slug}
            </div>
            <div className="hsub">
              {c.identity?.city || '—'} · {c.cameras || 0} câmeras ·{' '}
              {c.status === 'offline' ? `offline ${fmtAgo(c.last_seen)}` : `cpu ${Math.round(c.cpu_pct || 0)}%`}
            </div>
            <div className="hbars">
              {c.status === 'offline'
                ? services.length
                  ? services.map((s) => <i key={s} className="bad" />)
                  : [0, 1, 2].map((i) => <i key={i} className="bad" />)
                : services.map((s) => <i key={s} className={barClass(c, s)} />)}
            </div>
          </div>
        );
      })}
      {!courts.length && (
        <div className="report-empty">Nenhuma quadra reportou ainda — instale o agente nos rasps.</div>
      )}
    </div>
  );
}

/* ---------- relatório de erros do dia ---------- */
function RBlock({
  title, icon, count, children,
}: { title: string; icon: string; count: number; children: React.ReactNode }) {
  return (
    <div className="rblock">
      <h4><Icon name={icon} size={14} /> {title} <span className="n">{count}</span></h4>
      {count === 0 ? <div className="report-empty">Nada por aqui — tudo certo.</div> : children}
    </div>
  );
}

export function DailyReportCard({ onOpenCourt }: { onOpenCourt: (slug: string) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: report, isLoading } = useQuery({
    queryKey: ['report-today'],
    queryFn: getReportToday,
    staleTime: 10 * 60 * 1000,
  });
  const regen = useMutation({
    mutationFn: regenerateReport,
    onSuccess: (r) => {
      queryClient.setQueryData(['report-today'], r);
      toast('Relatório regerado');
    },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });

  if (isLoading) return <div className="report"><div className="loading-page"><span className="spinner" /> gerando relatório…</div></div>;
  if (!report) return null;

  return (
    <div className="report">
      <div className="report-head">
        <div>
          <h3>Relatório de erros — {report.date}</h3>
          <div className="when">
            gerado {fmtAgo(report.generated_at)} · 1× por dia via agendador · {report.trigger || ''}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => regen.mutate()} disabled={regen.isPending}>
          {regen.isPending ? <span className="spinner" /> : <Icon name="reset" size={14} />} Regerar agora
        </button>
      </div>
      <div className="report-grid">
        <RBlock title="Quadras offline" icon="power" count={report.quadras_offline.length}>
          {report.quadras_offline.map((q) => (
            <div key={q.slug} className="rline" style={{ cursor: 'pointer' }} onClick={() => onOpenCourt(q.slug)}>
              <span className="dot bad" /><span className="who">{q.court || q.slug}</span>
              <span className="meta bad">{fmtAgo(q.last_seen)}</span>
            </div>
          ))}
        </RBlock>
        <RBlock title="Services caídos" icon="alert" count={report.services_down.length}>
          {report.services_down.map((s, i) => (
            <div key={i} className="rline" style={{ cursor: 'pointer' }} onClick={() => onOpenCourt(s.slug)}>
              <span className="dot warn" /><span className="who">{s.slug}</span>
              <span className="meta warn">{serviceLabel(s.service)}</span>
            </div>
          ))}
        </RBlock>
        <RBlock title="CPU / temperatura altas" icon="chip" count={report.cpu_alerts.length + report.temp_alerts.length}>
          {report.cpu_alerts.map((a) => (
            <div key={`c${a.slug}`} className="rline"><span className="dot warn" />
              <span className="who">{a.slug}</span><span className="meta warn">CPU {a.cpu_pct}%</span></div>
          ))}
          {report.temp_alerts.map((a) => (
            <div key={`t${a.slug}`} className="rline"><span className="dot warn" />
              <span className="who">{a.slug}</span><span className="meta warn">{a.temp_c}°C {a.throttled && a.throttled !== '0x0' ? '· throttling' : ''}</span></div>
          ))}
        </RBlock>
        <RBlock title="Fila / vídeos com erro" icon="film" count={report.queue_backlogs.length + report.error_videos.length}>
          {report.queue_backlogs.map((a) => (
            <div key={`q${a.slug}`} className="rline"><span className="dot warn" />
              <span className="who">{a.slug}</span><span className="meta warn">{a.queue_depth} na fila</span></div>
          ))}
          {report.error_videos.map((a) => (
            <div key={`e${a.slug}`} className="rline"><span className="dot bad" />
              <span className="who">{a.slug}</span><span className="meta bad">{a.error_videos} com erro</span></div>
          ))}
        </RBlock>
        <RBlock title="Sem vídeos hoje" icon="search" count={report.zero_video_courts.length}>
          {report.zero_video_courts.map((slug) => (
            <div key={slug} className="rline" style={{ cursor: 'pointer' }} onClick={() => onOpenCourt(slug)}>
              <span className="dot warn" /><span className="who">{slug}</span>
              <span className="meta">0 vídeos</span>
            </div>
          ))}
        </RBlock>
        <RBlock title="Previsão de risco" icon="spark" count={report.risk.length}>
          {report.risk.map((r) => (
            <div key={r.slug} className="rline" style={{ cursor: 'pointer' }} onClick={() => onOpenCourt(r.slug)}>
              <span className={`dot ${r.level === 'alto' ? 'bad' : 'warn'}`} />
              <span className="who">{r.court || r.slug}</span>
              <span className={`meta ${r.level === 'alto' ? 'bad' : 'warn'}`}>{r.reasons.join(' · ')}</span>
            </div>
          ))}
        </RBlock>
      </div>
    </div>
  );
}
